// src/pages/admin/AuditLog.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Chip,
    Stack,
    Autocomplete,
    TextField,
    CircularProgress,
    Card,
    CardContent,
    Grid,
    Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
    FilterList,
    Clear as ClearIcon,
    Timeline as TimelineIcon,
    Person as PersonIcon,
    CalendarToday as CalendarIcon,
    Assignment as AssignmentIcon,
    FileDownload as FileDownloadIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { auditAPI } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { useRTL } from '../../../hooks/useRTL';
import NoPrivilege from '../../../components/common/NoPrivilege';
import NoContent from '../../../components/common/NoContent';
import AuditLogsTable from './AuditLogsTable';
import AuditLogDetailsDialog from './AuditLogDetailsDialog';

const AuditLog = () => {
    const { t } = useTranslation();
    const { isRTL, cardGridProps, iconContainerProps, textContainerProps } = useRTL();
    const { user, isSuperAdmin } = useAuth();

    // Action options for filter
    const ACTION_OPTIONS = [
        { value: '', label: t('common.all', 'All Actions') },
        { value: 'CREATE', label: t('audit.create', 'Create') },
        { value: 'UPDATE', label: t('audit.update', 'Update') },
        { value: 'DELETE', label: t('audit.delete', 'Delete') },
        { value: 'STATUS_CHANGE', label: t('audit.statusChange', 'Status Change') }
    ];

    const SORT_OPTIONS = [
        { value: 'createdAt', label: t('common.timestamp', 'Timestamp') },
        { value: 'action', label: t('common.action', 'Action') },
        { value: 'user', label: t('common.user', 'User') }
    ];

    const ORDER_OPTIONS = [
        { value: 'desc', label: t('common.newestFirst', 'Newest First') },
        { value: 'asc', label: t('common.oldestFirst', 'Oldest First') }
    ];

    // Permission checks
    const canView = isSuperAdmin || user?.privileges?.some(
        p => p.module === 'audit' && p.operations.includes('view')
    );
    const canExport = canView;

    // Data State
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [stats, setStats] = useState({});
    const [statsLoading, setStatsLoading] = useState(false);
    const [privilegeError, setPrivilegeError] = useState(false);
    const [totalLogs, setTotalLogs] = useState(0);

    // Export State
    const [exporting, setExporting] = useState(false);

    // Pagination State
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Dialog State
    const [selectedLog, setSelectedLog] = useState(null);
    const [isDialogOpen, setDialogOpen] = useState(false);

    // Consolidated filter state - using dayjs objects for dates
    const [filters, setFilters] = useState({
        action: '',
        startDate: null,
        endDate: null,
        sortBy: 'createdAt',
        sortOrder: 'desc'
    });

    // Fetch logs with pagination and filters
    const fetchLogs = useCallback(async () => {
        setLoading(true);
        setPrivilegeError(false);
        setError('');

        try {
            const params = {
                page: page + 1,
                limit: rowsPerPage,
                sortBy: filters.sortBy,
                sortOrder: filters.sortOrder
            };

            if (filters.action) params.action = filters.action;
            if (filters.startDate) params.startDate = filters.startDate.format('YYYY-MM-DD');
            if (filters.endDate) params.endDate = filters.endDate.format('YYYY-MM-DD');

            const response = await auditAPI.getAppointmentLogs(params);
            const responseData = response.data;

            setLogs(responseData.data || []);
            setTotalLogs(responseData.pagination?.total || responseData.data?.length || 0);
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Error fetching audit logs';
            if (err.response?.status === 403 || errorMessage.toLowerCase().includes('insufficient privileges')) {
                setPrivilegeError(true);
            } else {
                setError(errorMessage);
                toast.error(errorMessage);
            }
            setLogs([]);
            setTotalLogs(0);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, filters]);

    // Fetch stats
    const fetchStats = useCallback(async () => {
        setStatsLoading(true);
        try {
            const params = {};
            if (filters.startDate) params.startDate = filters.startDate.format('YYYY-MM-DD');
            if (filters.endDate) params.endDate = filters.endDate.format('YYYY-MM-DD');

            const response = await auditAPI.getAuditStats(params);
            setStats(response.data.data || {});
        } catch (err) {
            console.error('Error fetching stats:', err);
        } finally {
            setStatsLoading(false);
        }
    }, [filters.startDate, filters.endDate]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    // Filter handlers
    const handleFilterChange = useCallback((key, value) => {
        setPage(0);
        setFilters(prev => ({ ...prev, [key]: value }));
    }, []);

    const handleClearFilters = useCallback(() => {
        setPage(0);
        setFilters({
            action: '',
            startDate: null,
            endDate: null,
            sortBy: 'createdAt',
            sortOrder: 'desc'
        });
    }, []);

    // Check if any filter is active
    const hasActiveFilters = filters.action || filters.startDate || filters.endDate;

    // Export handler
    const handleExport = async () => {
        setExporting(true);
        try {
            const params = {
                sortBy: filters.sortBy,
                sortOrder: filters.sortOrder
            };

            if (filters.action) params.action = filters.action;
            if (filters.startDate) params.startDate = filters.startDate.format('YYYY-MM-DD');
            if (filters.endDate) params.endDate = filters.endDate.format('YYYY-MM-DD');

            const response = await auditAPI.export(params);

            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            const date = new Date().toISOString().split('T')[0];
            link.download = `audit_logs_export_${date}.xlsx`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success(t('audit.exportSuccess', 'Audit logs exported successfully'));
        } catch (err) {
            console.error('Export error:', err);
            toast.error(err.response?.data?.message || t('audit.exportError', 'Failed to export audit logs'));
        } finally {
            setExporting(false);
        }
    };

    // Refresh handler
    const handleRefresh = useCallback(() => {
        fetchLogs();
        fetchStats();
    }, [fetchLogs, fetchStats]);

    // Dialog handlers
    const handleOpenDialog = useCallback((log) => {
        setSelectedLog(log);
        setDialogOpen(true);
    }, []);

    const handleCloseDialog = useCallback(() => {
        setDialogOpen(false);
        setSelectedLog(null);
    }, []);

    // Pagination handlers
    const handlePageChange = useCallback((event, newPage) => {
        setPage(newPage);
    }, []);

    const handleRowsPerPageChange = useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);

    // Format date for display
    const formatDateDisplay = (date) => {
        if (!date) return '';
        return date.format('YYYY-MM-DD');
    };

    // Permission check
    if (!canView || privilegeError) {
        return <NoPrivilege message={t('audit.noPrivilegeError', 'You do not have permission to view audit logs.')} />;
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ p: 3 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="h4" component="h1" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                            {t('audit.title', 'Appointment Audit Log')}
                        </Typography>
                        {!loading && (
                            <Chip
                                label={`${totalLogs} ${t('common.total', 'total')}`}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={handleRefresh}
                            disabled={loading}
                        >
                            {t('common.refresh', 'Refresh')}
                        </Button>
                        {canExport && (
                            <Button
                                variant="outlined"
                                startIcon={exporting ? <CircularProgress size={18} /> : <FileDownloadIcon />}
                                onClick={handleExport}
                                disabled={exporting || loading}
                            >
                                {exporting ? t('common.exporting', 'Exporting...') : t('common.export', 'Export')}
                            </Button>
                        )}
                    </Stack>
                </Box>

                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {/* Statistics Cards */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Box sx={cardGridProps}>
                                    <Box sx={textContainerProps}>
                                        <Typography color="textSecondary" gutterBottom sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                            {t('audit.totalLogs', 'Total Logs')}
                                        </Typography>
                                        <Typography variant="h4" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                            {statsLoading ? <CircularProgress size={24} /> : stats.totalLogs || totalLogs || 0}
                                        </Typography>
                                    </Box>
                                    <Box sx={iconContainerProps}>
                                        <TimelineIcon />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Box sx={cardGridProps}>
                                    <Box sx={textContainerProps}>
                                        <Typography color="textSecondary" gutterBottom sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                            {t('audit.mostActiveUser', 'Most Active User')}
                                        </Typography>
                                        <Typography variant="h6" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                            {statsLoading ? <CircularProgress size={20} /> : stats.userStats?.[0]?.username || 'N/A'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ ...iconContainerProps, color: 'primary.main' }}>
                                        <PersonIcon />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Box sx={cardGridProps}>
                                    <Box sx={textContainerProps}>
                                        <Typography color="textSecondary" gutterBottom sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                            {t('audit.mostCommonAction', 'Most Common Action')}
                                        </Typography>
                                        <Typography variant="h6" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                            {statsLoading ? <CircularProgress size={20} /> : stats.actionStats?.[0]?._id || 'N/A'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ ...iconContainerProps, color: 'secondary.main' }}>
                                        <AssignmentIcon />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Box sx={cardGridProps}>
                                    <Box sx={textContainerProps}>
                                        <Typography color="textSecondary" gutterBottom sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                            {t('audit.dateRange', 'Date Range')}
                                        </Typography>
                                        <Typography variant="body1" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                            {filters.startDate && filters.endDate
                                                ? `${formatDateDisplay(filters.startDate)} - ${formatDateDisplay(filters.endDate)}`
                                                : t('common.allTime', 'All Time')}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ ...iconContainerProps, color: 'info.main' }}>
                                        <CalendarIcon />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Filters Paper */}
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Stack direction="row" alignItems="center" gap={2} mb={2}>
                        <FilterList />
                        <Typography variant="h6">{t('common.filters', 'Filters')}</Typography>
                        {hasActiveFilters && (
                            <Chip
                                label={t('common.filtersApplied', 'Filters applied')}
                                size="small"
                                color="info"
                                variant="outlined"
                            />
                        )}
                    </Stack>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" flexWrap="wrap">
                        {/* Action Filter */}
                        <Autocomplete
                            options={ACTION_OPTIONS}
                            getOptionLabel={(option) => option.label}
                            value={ACTION_OPTIONS.find(opt => opt.value === filters.action) || ACTION_OPTIONS[0]}
                            onChange={(event, newValue) => handleFilterChange('action', newValue?.value || '')}
                            renderInput={(params) => (
                                <TextField {...params} label={t('common.action', 'Action')} size="small" />
                            )}
                            sx={{ minWidth: 180 }}
                            disableClearable
                        />

                        {/* Start Date - DatePicker */}
                        <DatePicker
                            label={t('common.startDate', 'Start Date')}
                            value={filters.startDate}
                            onChange={(newValue) => handleFilterChange('startDate', newValue)}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    sx: { minWidth: 180 }
                                },
                                field: {
                                    clearable: true,
                                    onClear: () => handleFilterChange('startDate', null)
                                }
                            }}
                            maxDate={filters.endDate || undefined}
                            format="YYYY-MM-DD"
                        />

                        {/* End Date - DatePicker */}
                        <DatePicker
                            label={t('common.endDate', 'End Date')}
                            value={filters.endDate}
                            onChange={(newValue) => handleFilterChange('endDate', newValue)}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    sx: { minWidth: 180 }
                                },
                                field: {
                                    clearable: true,
                                    onClear: () => handleFilterChange('endDate', null)
                                }
                            }}
                            minDate={filters.startDate || undefined}
                            format="YYYY-MM-DD"
                        />

                        {/* Sort By */}
                        <Autocomplete
                            options={SORT_OPTIONS}
                            getOptionLabel={(option) => option.label}
                            value={SORT_OPTIONS.find(opt => opt.value === filters.sortBy) || SORT_OPTIONS[0]}
                            onChange={(event, newValue) => handleFilterChange('sortBy', newValue?.value || 'createdAt')}
                            renderInput={(params) => (
                                <TextField {...params} label={t('common.sortBy', 'Sort By')} size="small" />
                            )}
                            sx={{ minWidth: 150 }}
                            disableClearable
                        />

                        {/* Sort Order */}
                        <Autocomplete
                            options={ORDER_OPTIONS}
                            getOptionLabel={(option) => option.label}
                            value={ORDER_OPTIONS.find(opt => opt.value === filters.sortOrder) || ORDER_OPTIONS[0]}
                            onChange={(event, newValue) => handleFilterChange('sortOrder', newValue?.value || 'desc')}
                            renderInput={(params) => (
                                <TextField {...params} label={t('common.order', 'Order')} size="small" />
                            )}
                            sx={{ minWidth: 150 }}
                            disableClearable
                        />

                        {/* Clear Filters */}
                        {hasActiveFilters && (
                            <Button
                                variant="text"
                                color="inherit"
                                onClick={handleClearFilters}
                                startIcon={<ClearIcon />}
                                size="small"
                            >
                                {t('common.clearFilters', 'Clear')}
                            </Button>
                        )}
                    </Stack>
                </Paper>

                {/* Active Filters Display */}
                {hasActiveFilters && (
                    <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                        {filters.action && (
                            <Chip
                                label={`${t('common.action', 'Action')}: ${ACTION_OPTIONS.find(o => o.value === filters.action)?.label}`}
                                size="small"
                                color={
                                    filters.action === 'CREATE' ? 'success' :
                                        filters.action === 'DELETE' ? 'error' :
                                            filters.action === 'UPDATE' ? 'primary' : 'warning'
                                }
                                onDelete={() => handleFilterChange('action', '')}
                            />
                        )}
                        {filters.startDate && (
                            <Chip
                                label={`${t('common.from', 'From')}: ${formatDateDisplay(filters.startDate)}`}
                                size="small"
                                onDelete={() => handleFilterChange('startDate', null)}
                            />
                        )}
                        {filters.endDate && (
                            <Chip
                                label={`${t('common.to', 'To')}: ${formatDateDisplay(filters.endDate)}`}
                                size="small"
                                onDelete={() => handleFilterChange('endDate', null)}
                            />
                        )}
                    </Box>
                )}

                {/* Results Info */}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('common.showing', 'Showing')} {totalLogs === 0 ? 0 : Math.min(page * rowsPerPage + 1, totalLogs)}-{Math.min((page + 1) * rowsPerPage, totalLogs)} {t('common.of', 'of')} {totalLogs} {t('common.items', 'items')}
                </Typography>

                {/* Table / No Content */}
                {logs.length === 0 && !loading ? (
                    <Paper sx={{ p: 4 }}>
                        <NoContent
                            icon={<TimelineIcon sx={{ fontSize: 64 }} />}
                            title={
                                hasActiveFilters
                                    ? t('audit.noFilterResults', 'No audit logs match your filters')
                                    : t('audit.noLogsTitle', 'No Audit Logs Found')
                            }
                            description={
                                hasActiveFilters
                                    ? t('audit.tryAdjustingFilters', 'Try adjusting your filter criteria')
                                    : t('audit.noLogsDescription', 'Audit logs will appear here when actions are performed.')
                            }
                        />
                    </Paper>
                ) : (
                    <AuditLogsTable
                        logs={logs}
                        loading={loading}
                        onViewDetails={handleOpenDialog}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        total={totalLogs}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        filters={filters}
                        isRTL={isRTL}
                    />
                )}

                {/* Details Dialog */}
                <AuditLogDetailsDialog
                    open={isDialogOpen}
                    onClose={handleCloseDialog}
                    log={selectedLog}
                />
            </Box>
        </LocalizationProvider>
    );
};

export default AuditLog;